/** Where a course sits in time. Separate from whether a visitor may enrol. */
export const CourseTimeStatus = {
  UPCOMING: 'UPCOMING',
  IN_PROGRESS: 'IN_PROGRESS',
  ENDED: 'ENDED'
};

// `no-redeclare` only understands interface and class merging, so it flags a
// value and a type sharing a name. Callers want both.
// eslint-disable-next-line @typescript-eslint/no-redeclare

/** `new Date('nonsense')` passes a `!date` check but fails every comparison. */
export const toCourseDate = value => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? undefined : date;
};
const hasPassed = (date, now) => date.valueOf() < now.valueOf();

/**
 * `<=`, because a course whose first session starts right now has started.
 *
 * The Service Page read that instant as IN_PROGRESS and the Timetable as
 * UPCOMING, so they disagreed for exactly one millisecond.
 */
const hasStarted = (date, now) => date.valueOf() <= now.valueOf();

/**
 * The server answer wins: `service.schedule` is a copy that a dropped Kafka
 * message can leave stale.
 *
 * The two sources are never mixed - whichever supplies the start supplies the
 * end. Per-field fallback would overwrite an absent end with a stale one and
 * read ENDED on a course that is still running.
 */
export const getCourseDateRange = (service, scheduleDates) => {
  var _service$schedule, _service$schedule2;
  const serverStart = toCourseDate(scheduleDates == null ? void 0 : scheduleDates.firstSessionStart);
  if (serverStart) {
    return {
      start: serverStart,
      end: toCourseDate(scheduleDates == null ? void 0 : scheduleDates.lastSessionEnd)
    };
  }
  return {
    start: toCourseDate((_service$schedule = service.schedule) == null ? void 0 : _service$schedule.firstSessionStart),
    end: toCourseDate((_service$schedule2 = service.schedule) == null ? void 0 : _service$schedule2.lastSessionEnd)
  };
};

/** Exported on its own because each widget picks its dates differently. */
export const getCourseTimeStatusFromDates = function (_ref, now) {
  let {
    start,
    end
  } = _ref;
  if (now === void 0) {
    now = new Date();
  }
  // Only a KNOWN end date that has passed. No end date means the course
  // repeats and is still running, so ENDED would hide it.
  if (end && hasPassed(end, now)) {
    return 'ENDED';
  }

  // A missing start counts as already started.
  return !start || hasStarted(start, now) ? 'IN_PROGRESS' : 'UPCOMING';
};
export const getCourseTimeStatus = function (service, now, scheduleDates) {
  if (now === void 0) {
    now = new Date();
  }
  return getCourseTimeStatusFromDates(getCourseDateRange(service, scheduleDates), now);
};
const SEPARATOR = ' - ';

// Without a zone Intl uses the machine zone: UTC on the server, the visitor's
// in the browser, which mismatches on hydration.
const FALLBACK_TIME_ZONE = 'UTC';
const formatDate = (date, locale, timezone) => new Intl.DateTimeFormat(locale, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: timezone ?? FALLBACK_TIME_ZONE
}).format(date);

/**
 * `Jun 22, 2026 - Dec 20, 2026`, or the start alone for a course that repeats
 * with no end date. The shared date helpers do not fit: one drops the year,
 * the other adds a weekday.
 *
 * `locale` must be the DATE regional locale, never the content one.
 */
export const formatCourseDateRange = (_ref2, locale, timezone) => {
  let {
    start,
    end
  } = _ref2;
  if (!start) {
    return undefined;
  }
  const from = formatDate(start, locale, timezone);
  return end ? `${from}${SEPARATOR}${formatDate(end, locale, timezone)}` : from;
};
//# sourceMappingURL=course.js.map